import type {
  CmmReadinessFacts,
  CmmReadinessLine,
} from '../../contracts/cmm-gateway.js';
import {
  CMM_READINESS_CHECK_LABELS,
  CMM_READINESS_SEED,
} from '../../contracts/schemas/readiness.js';
import {
  type CheckStatus,
  type ReadinessCheck,
  summariseReadiness,
  type WorkflowReadiness,
} from './readiness.js';

/**
 * The one definition of "is this order ready for CMM".
 *
 * Same split as the tracing version: `CMM_READINESS_SEED` is the list and its
 * severities, this file is what each key verifies. The readiness drawer, the
 * order list column and the send preflight all read these two constants, so no
 * database row can make them disagree.
 *
 * The one structural difference from tracing is the unit of the answer. Tracing
 * asks whether an *order* can go out; CMM claims lines one at a time and marks
 * each with its own `cmm_order_line_id`, so a verdict that only describes the
 * whole order cannot tell the user which line to fix. `computeCmmReadiness`
 * therefore returns a per-line result alongside the rolled-up one, from a single
 * pass over the same evaluators — not a second opinion computed separately.
 *
 * A caller has no "still loading" state to report, so anything unresolvable is
 * `unknown` and excluded from the missing list.
 */

/** A check answered once for the order. */
export interface CmmOrderCheckEvaluator {
  group: 'order';
  /** `false` drops the check entirely rather than reporting it unmet. */
  appliesTo?: (facts: CmmReadinessFacts) => boolean;
  status: (facts: CmmReadinessFacts) => CheckStatus;
}

/** A check answered once per line. */
export interface CmmLineCheckEvaluator {
  group: 'line';
  appliesTo?: (facts: CmmReadinessFacts) => boolean;
  status: (line: CmmReadinessLine) => CheckStatus;
}

export type CmmCheckEvaluator = CmmOrderCheckEvaluator | CmmLineCheckEvaluator;

/** One line's own verdict. */
export interface CmmLineReadiness {
  /** `order_lines.id` — what the send addresses the line by. */
  orderLineId: string;
  ready: boolean;
  /**
   * Required keys this line fails, in seed order.
   *
   * Order-level gaps are included: a missing supplier link stops every line, and
   * a list that omitted it would show a line as fixable when nothing about the
   * line is wrong.
   */
  missing: string[];
  /** Order-level checks first, then this line's own, both in seed order. */
  checks: ReadinessCheck[];
}

/**
 * The rolled-up order verdict, plus each line's.
 *
 * Extends `WorkflowReadiness` so the readiness drawer renders a CMM card with
 * the component it already uses for tracing.
 */
export interface CmmReadiness extends WorkflowReadiness {
  lines: CmmLineReadiness[];
}

function filled(value: string | null): CheckStatus {
  return value != null && value.trim() !== '' ? 'met' : 'missing';
}

function bool(met: boolean): CheckStatus {
  return met ? 'met' : 'missing';
}

/** Percentages are stored as `numeric`, so a composition that a person entered
 *  as 100 can come back as 99.999999. */
const COMPOSITION_TOTAL = 100;
const COMPOSITION_TOLERANCE = 0.01;

/**
 * `null` is "the style has no material lines at all" — a gap someone can close,
 * not a sum we failed to read, so it is `missing` rather than `unknown`.
 */
function materialComposition(line: CmmReadinessLine): CheckStatus {
  if (line.materialCompositionSum == null) return 'missing';
  return bool(
    line.materialCompositionSum >= COMPOSITION_TOTAL - COMPOSITION_TOLERANCE,
  );
}

/**
 * One evaluator per key in `CMM_READINESS_SEED`, kept in step with it by a test.
 *
 * Not reachable from the automation gate today. That gate resolves a requirement
 * key through a single flat registry over a single fact type, and these read
 * `CmmReadinessFacts` — see the note at the top of this file.
 */
export const CMM_CHECK_EVALUATORS: Record<string, CmmCheckEvaluator> = {
  supplier_linked: {
    group: 'order',
    status: facts => filled(facts.order.supplierCompanyId),
  },
  supplier_identifier: {
    group: 'order',
    // An unreadable counterparty profile is `unknown`, not empty: a code we
    // could not fetch is not evidence that there is no code.
    status: facts =>
      facts.seller.resolved
        ? filled(facts.seller.internalCompanyCode)
        : 'unknown',
  },
  product_reference: {
    group: 'line',
    status: line => bool(!!line.styleId),
  },
  material_composition: {
    group: 'line',
    status: materialComposition,
  },
  certification_standard: {
    group: 'line',
    status: line => bool(line.claimIntentions.length > 0),
  },
  quantity_unit: {
    group: 'line',
    // One key, both halves: CMM stores `quantity_number` and `quantity_uom`
    // together and a quantity without its unit is not a quantity.
    status: line => bool(line.quantity != null && filled(line.unit) === 'met'),
  },
};

/**
 * One status for a line check across every line.
 *
 * An order with no lines is `missing`: there is nothing to send, and CMM's list
 * has no separate "at least one order line" key to say so.
 *
 * `missing` outranks `unknown` so the summary blames something the user can
 * actually fix when both are present.
 */
function rollUpLines(statuses: CheckStatus[]): CheckStatus {
  if (statuses.length === 0) return 'missing';
  if (statuses.includes('missing')) return 'missing';
  if (statuses.includes('unknown')) return 'unknown';
  return 'met';
}

function requiredMissing(checks: ReadinessCheck[]): string[] {
  return checks
    .filter(c => c.severity === 'required' && c.status === 'missing')
    .map(c => c.key);
}

/**
 * Evaluates every seeded CMM check once, and reports the answer at both levels.
 *
 * Takes the whole facts object rather than loose arguments: `loadCmmReadinessFacts`
 * is the only thing allowed to assemble it, which is what stops one caller
 * forgetting `materialCompositionSum` and silently skipping a check.
 */
export function computeCmmReadiness(facts: CmmReadinessFacts): CmmReadiness {
  const orderChecks: ReadinessCheck[] = [];
  const rolledUp: ReadinessCheck[] = [];
  // Indexed by position, not keyed by `orderLineId`: two rows can carry the same
  // identifier only if something upstream is already broken, and a map would
  // silently merge their verdicts instead of reporting both.
  const lineChecks: ReadinessCheck[][] = facts.lines.map(() => []);

  for (const entry of CMM_READINESS_SEED) {
    const evaluator = CMM_CHECK_EVALUATORS[entry.key];
    if (evaluator.appliesTo && !evaluator.appliesTo(facts)) continue;

    if (evaluator.group === 'order') {
      const check: ReadinessCheck = {
        key: entry.key,
        status: evaluator.status(facts),
        group: 'order',
        severity: entry.severity,
      };
      orderChecks.push(check);
      rolledUp.push(check);
      continue;
    }

    const statuses = facts.lines.map((line, index) => {
      const status = evaluator.status(line);
      lineChecks[index].push({
        key: entry.key,
        status,
        group: 'line',
        severity: entry.severity,
      });
      return status;
    });

    rolledUp.push({
      key: entry.key,
      status: rollUpLines(statuses),
      group: 'line',
      severity: entry.severity,
    });
  }

  const lines = facts.lines.map<CmmLineReadiness>((line, index) => {
    const checks = [...orderChecks, ...lineChecks[index]];
    const missing = requiredMissing(checks);

    return {
      orderLineId: line.orderLineId,
      ready: missing.length === 0,
      missing,
      checks,
    };
  });

  return { ...summariseReadiness(rolledUp), lines };
}

/**
 * How many of `lines` fail each line-level check, keyed by check key.
 *
 * The aggregate a list row carries instead of its lines. The same evaluators
 * the send runs, so a row cannot pass a check the send then fails.
 */
export function countCmmLineGaps(
  lines: CmmReadinessLine[],
): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const entry of CMM_READINESS_SEED) {
    const evaluator = CMM_CHECK_EVALUATORS[entry.key];
    if (evaluator.group !== 'line') continue;
    counts[entry.key] = lines.filter(
      line => evaluator.status(line) === 'missing',
    ).length;
  }
  return counts;
}

/** Required gaps to print. `unknown` means "could not evaluate", not "missing". */
export function cmmMissingRequirementLabels(
  readiness: Pick<CmmReadiness, 'checks'>,
): string[] {
  return requiredMissing(readiness.checks).map(
    key => CMM_READINESS_CHECK_LABELS[key] ?? key,
  );
}
